import React, { useMemo, useState } from "react";
import { Button, Loader } from "components";
import { ENDPOINTS } from "const";
import { useDeleteUser, useUsers } from "hooks";
import { useQueryClient } from "react-query";
import { IUser, USER_ROLES } from "types";

function getDisplayName(user: IUser) {
  if (user.firstName || user.lastName) {
    return [user.firstName, user.lastName].filter(Boolean).join(" ");
  }
  return user.email;
}

export function UserManagementSection() {
  const queryClient = useQueryClient();
  const { data: users, isLoading } = useUsers({ limit: 100, page: 1 });
  const { mutateAsync: removeUser, isLoading: isDeleting } = useDeleteUser();
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const items = useMemo(() => users?.data?.items ?? [], [users]);
  const total = users?.data?.meta?.totalItems ?? items.length;

  const handleDelete = async (id?: string) => {
    if (!id) {
      return;
    }
    setPendingDelete(id);
    try {
      await removeUser(id);
      queryClient.invalidateQueries([ENDPOINTS.USERS]);
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <section className="flex flex-col gap-6">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
            <p className="text-sm text-gray-600">
              Review community members, tidy inactive accounts, and promote collaborators to
              administrators when required.
            </p>
          </div>
          <div className="rounded-md bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700">
            {total} users
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        {isLoading && <Loader type="rings" />}
        {!isLoading && items.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
            No users found yet. They will appear here once the community starts signing in.
          </div>
        )}
        {items.map((user) => {
          const isCurrent = pendingDelete === user.id;
          const roles = user.roles?.length ? user.roles : [USER_ROLES.USER];
          return (
            <article
              key={user.id}
              className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between"
            >
              <div className="flex flex-col gap-1">
                <span className="text-base font-semibold text-gray-900">
                  {getDisplayName(user)}
                </span>
                <span className="text-sm text-gray-500">{user.email}</span>
                <div className="flex flex-wrap gap-2 pt-1">
                  {roles.map((role) => (
                    <span
                      key={role}
                      className="rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  disabled={!user.id || isDeleting || isCurrent}
                  onClick={() => handleDelete(user.id)}
                  isReverse
                >
                  {isCurrent ? "Removing…" : "Delete"}
                </Button>
              </div>
            </article>
          );
        })}
        {isDeleting && <Loader type="rings" />}
      </div>
    </section>
  );
}
